import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import Header from '../../components/ui/Header';
import MobileBottomNav from '../../components/ui/MobileBottomNav';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import Select from '../../components/ui/Select';
import ReconciliationSummary from './components/ReconciliationSummary';
import DrawerCountsList from './components/DrawerCountsList';
import DiscrepancyAlerts from './components/DiscrepancyAlerts';
import SettlementConfirmation from './components/SettlementConfirmation';
import { openaiService } from '../../services/openaiService';
import { emailService } from '../../services/emailService';

const DailyCashReconciliationReports = () => {
  const navigate = useNavigate();
  const { user, currentTenant, isGymOwner, isStaff, loading: authLoading } = useAuth();
  const [loading, setLoading] = useState(true);
  const [reconciliations, setReconciliations] = useState([]);
  const [selectedDate, setSelectedDate] = useState(new Date()?.toISOString()?.split('T')?.[0]);
  const [stats, setStats] = useState({
    totalDrawers: 0,
    totalExpected: 0,
    totalActual: 0,
    totalVariance: 0,
    pendingApprovals: 0,
    approvedCount: 0
  });
  const [filterStatus, setFilterStatus] = useState('all');
  const [showSettlementModal, setShowSettlementModal] = useState(false);
  const [selectedReconciliation, setSelectedReconciliation] = useState(null);
  const [varianceThreshold, setVarianceThreshold] = useState(5); // Default $5 threshold
  const [autoAlertEnabled, setAutoAlertEnabled] = useState(true);
  const [analyzingVariance, setAnalyzingVariance] = useState(false);

  useEffect(() => {
    if (!authLoading && (!user || (!isGymOwner && !isStaff))) {
      navigate('/member-dashboard');
    }
  }, [user, isGymOwner, isStaff, authLoading, navigate]);

  useEffect(() => {
    if (currentTenant?.id && (isGymOwner || isStaff)) {
      loadReconciliationData();
    }
  }, [currentTenant, selectedDate, filterStatus]);

  const loadReconciliationData = async () => {
    setLoading(true);
    try {
      // Load reconciliations for selected date
      let query = supabase
        ?.from('cash_reconciliations')
        ?.select(`
          *,
          cash_drawers(id, name, opened_at, closed_at),
          user_profiles:reconciled_by(full_name, email),
          cash_denominations(denomination_type, denomination_value, quantity)
        `)
        ?.eq('tenant_id', currentTenant?.id)
        ?.gte('created_at', `${selectedDate}T00:00:00`)
        ?.lte('created_at', `${selectedDate}T23:59:59`)
        ?.order('created_at', { ascending: false });

      if (filterStatus !== 'all') {
        query = query?.eq('status', filterStatus);
      }

      const { data, error } = await query;

      if (error) throw error;

      setReconciliations(data || []);

      // Calculate stats
      const totalExpected = data?.reduce((sum, r) => sum + parseFloat(r?.expected_balance || 0), 0);
      const totalActual = data?.reduce((sum, r) => sum + parseFloat(r?.actual_balance || 0), 0);
      const pendingCount = data?.filter(r => r?.status === 'pending')?.length;
      const approvedCount = data?.filter(r => r?.status === 'approved')?.length;

      setStats({
        totalDrawers: data?.length || 0,
        totalExpected,
        totalActual,
        totalVariance: totalActual - totalExpected,
        pendingApprovals: pendingCount,
        approvedCount
      });

      // Check for variances exceeding threshold and trigger alerts
      if (autoAlertEnabled) {
        await checkAndTriggerVarianceAlerts(data);
      }
    } catch (error) {
      console.error('Error loading reconciliation data:', error);
    } finally {
      setLoading(false);
    }
  };

  /**
   * Check variances and trigger AI analysis + email alerts
   */
  const checkAndTriggerVarianceAlerts = async (reconciliations) => {
    try {
      for (const reconciliation of reconciliations) {
        const variance = reconciliation?.actual_balance - reconciliation?.expected_balance;
        
        // Only process if variance exceeds threshold and hasn't been alerted yet
        if (Math.abs(variance) >= varianceThreshold && !reconciliation?.alert_sent) {
          await analyzeAndAlertVariance(reconciliation, variance);
        }
      }
    } catch (error) {
      console.error('Error checking variance alerts:', error);
    }
  };

  /**
   * Analyze variance with OpenAI and send email alert
   */
  const analyzeAndAlertVariance = async (reconciliation, variance) => {
    try {
      setAnalyzingVariance(true);

      // Fetch related transactions for analysis
      const { data: transactions } = await supabase
        ?.from('cash_transactions')
        ?.select('*')
        ?.eq('drawer_id', reconciliation?.drawer_id)
        ?.gte('created_at', reconciliation?.cash_drawers?.opened_at)
        ?.lte('created_at', reconciliation?.created_at)
        ?.order('created_at', { ascending: false });

      // Fetch historical variances for pattern analysis
      const { data: historicalVariances } = await supabase
        ?.from('cash_reconciliations')
        ?.select('variance, created_at')
        ?.eq('drawer_id', reconciliation?.drawer_id)
        ?.neq('id', reconciliation?.id)
        ?.order('created_at', { ascending: false })
        ?.limit(10);

      // Calculate shift duration
      const shiftDuration = reconciliation?.cash_drawers?.opened_at && reconciliation?.cash_drawers?.closed_at
        ? Math.round((new Date(reconciliation.cash_drawers.closed_at) - new Date(reconciliation.cash_drawers.opened_at)) / (1000 * 60 * 60))
        : null;

      // Analyze with OpenAI
      const aiResult = await openaiService?.analyzeCashDiscrepancy({
        variance,
        expectedBalance: reconciliation?.expected_balance,
        actualBalance: reconciliation?.actual_balance,
        transactions: transactions || [],
        drawerName: reconciliation?.cash_drawers?.name,
        reconciledBy: reconciliation?.user_profiles?.full_name,
        shiftDuration: shiftDuration ? `${shiftDuration} hours` : 'Unknown',
        historicalVariances: historicalVariances?.map(hv => ({
          variance: hv?.variance,
          date: hv?.created_at
        })) || []
      });

      if (aiResult?.success) {
        // Send email alert with AI analysis
        const emailResult = await emailService?.sendCashVarianceAlert({
          reconciliationId: reconciliation?.id,
          variance,
          expectedBalance: reconciliation?.expected_balance,
          actualBalance: reconciliation?.actual_balance,
          drawerName: reconciliation?.cash_drawers?.name,
          reconciledBy: reconciliation?.user_profiles?.full_name,
          reconciledByEmail: reconciliation?.user_profiles?.email,
          tenantName: currentTenant?.name,
          tenantId: currentTenant?.id,
          aiAnalysis: aiResult?.analysis,
          transactionPatterns: aiResult?.analysis?.transactionPatterns,
          suspiciousFlags: aiResult?.analysis?.suspiciousPatterns
        });

        if (emailResult?.success) {
          // Mark reconciliation as alerted
          await supabase
            ?.from('cash_reconciliations')
            ?.update({ 
              alert_sent: true, 
              alert_sent_at: new Date()?.toISOString(),
              ai_analysis: aiResult?.analysis
            })
            ?.eq('id', reconciliation?.id);

          console.log(`Variance alert sent for reconciliation ${reconciliation?.id}`);
        }
      }
    } catch (error) {
      console.error('Error analyzing and alerting variance:', error);
    } finally {
      setAnalyzingVariance(false);
    }
  };

  /**
   * Manual trigger for variance analysis and alert
   */
  const handleManualVarianceAlert = async (reconciliation) => {
    const variance = reconciliation?.actual_balance - reconciliation?.expected_balance;
    await analyzeAndAlertVariance(reconciliation, variance);
    await loadReconciliationData(); // Reload to show updated status
  };

  const handleApproveReconciliation = async (reconciliationId) => {
    try {
      const { error } = await supabase
        ?.from('cash_reconciliations')
        ?.update({ status: 'approved', approved_by: user?.id, approved_at: new Date()?.toISOString() })
        ?.eq('id', reconciliationId);

      if (error) throw error;

      alert('Reconciliation approved successfully');
      loadReconciliationData();
    } catch (error) {
      console.error('Error approving reconciliation:', error);
      alert('Failed to approve reconciliation');
    }
  };

  const handleDisputeReconciliation = async (reconciliationId, reason) => {
    try {
      const { error } = await supabase
        ?.from('cash_reconciliations')
        ?.update({ 
          status: 'disputed', 
          dispute_reason: reason,
          disputed_by: user?.id, 
          disputed_at: new Date()?.toISOString() 
        })
        ?.eq('id', reconciliationId);

      if (error) throw error;

      alert('Reconciliation marked as disputed');
      loadReconciliationData();
    } catch (error) {
      console.error('Error disputing reconciliation:', error);
      alert('Failed to dispute reconciliation');
    }
  };

  const handleGenerateSettlement = (reconciliation) => {
    setSelectedReconciliation(reconciliation);
    setShowSettlementModal(true);
  };

  const handleExportReport = async () => {
    try {
      // Generate CSV export
      const csvHeaders = 'Drawer,Expected Balance,Actual Balance,Variance,Status,Reconciled By,Time\n';
      const csvRows = reconciliations?.map(r => 
        `${r?.cash_drawers?.name || 'N/A'},${r?.expected_balance},${r?.actual_balance},${(r?.actual_balance - r?.expected_balance)?.toFixed(2)},${r?.status},${r?.user_profiles?.full_name || 'N/A'},${new Date(r?.created_at)?.toLocaleTimeString()}`
      )?.join('\n');
      
      const csvContent = csvHeaders + csvRows;
      const blob = new Blob([csvContent], { type: 'text/csv' });
      const url = window.URL?.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `cash-reconciliation-${selectedDate}.csv`;
      link?.click();
      window.URL?.revokeObjectURL(url);
    } catch (error) {
      console.error('Export error:', error);
      alert('Failed to export report');
    }
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <Icon name="Loader" size={48} className="animate-spin text-primary mx-auto mb-4" />
          <p className="text-muted-foreground">Loading reconciliation data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="container mx-auto px-4 py-6 pb-24 lg:pb-6 max-w-7xl">
        {/* Page Header */}
        <div className="mb-6">
          <div className="flex items-center gap-3 mb-2">
            <Icon name="Calculator" size={32} className="text-primary" />
            <h1 className="text-3xl font-bold text-foreground">Daily Cash Reconciliation Reports</h1>
          </div>
          <p className="text-muted-foreground">Track drawer counts, identify discrepancies, and confirm daily settlements</p>
        </div>

        {/* Filters and Actions */}
        <div className="bg-card rounded-lg shadow-md p-4 mb-6 border border-border">
          <div className="flex flex-col md:flex-row gap-4 items-start md:items-center justify-between">
            <div className="flex flex-col sm:flex-row gap-3 flex-1">
              <div className="flex-1 min-w-[200px]">
                <label className="block text-sm font-medium text-foreground mb-1">Date</label>
                <input
                  type="date"
                  value={selectedDate}
                  onChange={(e) => setSelectedDate(e?.target?.value)}
                  className="w-full px-3 py-2 rounded-lg bg-background border border-border focus:outline-none focus:ring-2 focus:ring-primary text-foreground"
                />
              </div>
              <div className="flex-1 min-w-[200px]">
                <label className="block text-sm font-medium text-foreground mb-1">Status Filter</label>
                <Select
                  value={filterStatus}
                  onChange={(e) => setFilterStatus(e?.target?.value)}
                  options={[
                    { value: 'all', label: 'All Status' },
                    { value: 'pending', label: 'Pending Approval' },
                    { value: 'approved', label: 'Approved' },
                    { value: 'disputed', label: 'Disputed' }
                  ]}
                />
              </div>
            </div>
            <div className="flex gap-2">
              <Button
                onClick={handleExportReport}
                variant="outline"
                iconName="Download"
                iconPosition="left"
              >
                Export CSV
              </Button>
              <Button
                onClick={loadReconciliationData}
                iconName="RefreshCw"
                iconPosition="left"
              >
                Refresh
              </Button>
            </div>
          </div>
        </div>

        {/* Summary Dashboard */}
        <ReconciliationSummary stats={stats} />

        {/* Discrepancy Alerts */}
        {reconciliations?.filter(r => {
          const variance = r?.actual_balance - r?.expected_balance;
          return Math.abs(variance) >= varianceThreshold;
        })?.length > 0 && (
          <DiscrepancyAlerts 
            reconciliations={reconciliations?.filter(r => {
              const variance = r?.actual_balance - r?.expected_balance;
              return Math.abs(variance) >= varianceThreshold;
            })}
            onTriggerAlert={handleManualVarianceAlert}
            threshold={varianceThreshold}
          />
        )}

        {/* Drawer Counts List */}
        <DrawerCountsList
          reconciliations={reconciliations}
          onApprove={handleApproveReconciliation}
          onDispute={handleDisputeReconciliation}
          onGenerateSettlement={handleGenerateSettlement}
        />

        {/* Settlement Confirmation Modal */}
        {showSettlementModal && selectedReconciliation && (
          <SettlementConfirmation
            reconciliation={selectedReconciliation}
            onClose={() => {
              setShowSettlementModal(false);
              setSelectedReconciliation(null);
            }}
            onConfirm={() => {
              setShowSettlementModal(false);
              setSelectedReconciliation(null);
              loadReconciliationData();
            }}
          />
        )}
      </main>
      <MobileBottomNav />
    </div>
  );
};

export default DailyCashReconciliationReports;